import React, { useState, useEffect, useCallback } from 'react';
import { Search, Calendar, CheckSquare, Square, Filter, RefreshCw, X, AlertCircle, ChevronLeft, ChevronRight, AlertTriangle, Download } from 'lucide-react';

// Main Dashboard Component
const OrderUpdate = () => {
  // State for orders data
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [updateInProgress, setUpdateInProgress] = useState(false);
  const [exportInProgress, setExportInProgress] = useState(false);
  const [batchUpdateStatus, setBatchUpdateStatus] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  
  // State for business units dropdown
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loadingBusinessUnits, setLoadingBusinessUnits] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const RECORDS_PER_PAGE = 100;
  
  // NEW: Add state for cached pages and all matching order IDs
  const [cachedPages, setCachedPages] = useState({});
  const [allMatchingOrderIds, setAllMatchingOrderIds] = useState([]);
  const [selectAllInProgress, setSelectAllInProgress] = useState(false);
  
  // Get API URL from environment or default
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';
  
  // State for search criteria (what user types)
  const [searchCriteria, setSearchCriteria] = useState({
    orderNumber: '',
    businessUnit: '',
    po: '',
    wave: '',
    shipDate: '',
    orderRcvDateFrom: '',
    orderRcvDateTo: '',
    dueDateFrom: '',
    dueDateTo: ''
  });
  
  // State for Show Late filter
  const [showLateOnly, setShowLateOnly] = useState(false);
  
  // State for actual filters (what is sent to the API)
  const [activeFilters, setActiveFilters] = useState({
    orderNumber: '',
    businessUnit: '',
    po: '',
    wave: '',
    shipDate: '',
    orderRcvDateFrom: '',
    orderRcvDateTo: '',
    dueDateFrom: '',
    dueDateTo: '',
    showLateOnly: false
  });
  
  // State for selecting and editing
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newDueDate, setNewDueDate] = useState('');
  
  // State for accuracy modal
  const [isAccuracyModalOpen, setIsAccuracyModalOpen] = useState(false);
  
  // NEW: Added virtual selection state to track if a select all operation is active
  const [virtualSelectAll, setVirtualSelectAll] = useState(false);
  const [virtualSelectionCount, setVirtualSelectionCount] = useState(0);
  
  // The maximum number of orders that can be selected for bulk update - Increased to handle more records
  const MAX_SELECTABLE_ORDERS = 50000;
  
  // Batch size for updating large numbers of records - Increased to 5000 from 500
  const BATCH_SIZE = 5000;
  
  // Number of pages to cache on each side of current page
  const CACHE_PAGES_COUNT = 5;
  
  // Load initial orders on component mount
  useEffect(() => {
    fetchTotalCount();
    loadInitialOrders();
    fetchBusinessUnits();
  }, []);
  
  // Fetch business units for dropdown
  const fetchBusinessUnits = async () => {
    try {
      setLoadingBusinessUnits(true);
      
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
      // Fetch business units from API
      const response = await fetch(`${API_URL}/business-units`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch business units');
      }
      
      const units = await response.json();
      
      // Ensure we have both name and identifier
      const formattedUnits = units.map(unit => ({
        id: unit.id,
        name: unit.name || unit.id,
        identifier: unit.identifier || unit.id,
        displayName: `${unit.name || unit.id} - ${unit.identifier || unit.id}`
      }));
      
      setBusinessUnits(formattedUnits);
    } catch (err) {
      console.error('Failed to fetch business units:', err);
      // Continue without business units - will fallback to text input
    } finally {
      setLoadingBusinessUnits(false);
    }
  };
  
  // Fetch total number of orders for display
  const fetchTotalCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const response = await fetch(`${API_URL}/orders/count`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch order count');
      }
      
      const data = await response.json();
      setTotalOrderCount(data.count);
    } catch (err) {
      console.error("Failed to fetch total count:", err);
    }
  };
  
  // Load initial limited set of orders (first load without filters)
  const loadInitialOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      setWarning(null);
      
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Make API request with no filters, just the first page
      const response = await fetch(`${API_URL}/orders?page=1&limit=${RECORDS_PER_PAGE}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }
      
      const data = await response.json();
      
      // Map database fields to component fields
      // Using Updated_Due_Date with fallback to Corrected_Due_Date
      const formattedData = data.records.map((order, index) => ({
        id: order.id || order['Ord #'], // Use order number as ID, not index
        orderNumber: order['Ord #'],
        businessUnit: order['Business Unit'],
        po: order['PO'],
        wave: order['Wave'],
        // Remove shipDate field from the mapped data
        receivedDate: order['Order RCV Date'],
        dueDate: order['Updated_Due_Date'] || order['Corrected_Due_Date'], // Prefer Updated_Due_Date, fall back to Corrected_Due_Date
        orderClosedTStamp: order['Order Closed TStamp'],
        orderStatus: order['Order_Status'],
        inaccurateOrder: order['inaccurate_order'] || 0, // Add accuracy status
      }));
      
      setOrders(formattedData);
      setFilteredOrders(formattedData);
      setCurrentPage(data.pagination.page);
      setTotalPages(data.pagination.totalPages);
      setTotalRecords(data.pagination.totalRecords);
      
      // Store first page in cache
      setCachedPages({
        1: formattedData
      });
      
      // Don't set allMatchingOrderIds here - it will be populated when user clicks Select All
      setAllMatchingOrderIds([]);
      
      if (data.warning) {
        setWarning(data.warning);
      }
      
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load orders data');
      setLoading(false);
    }
  };
  
  // Function to fetch matching order IDs for Select All
  const fetchAllMatchingOrderIds = async () => {
    try {
      setSelectAllInProgress(true);
      
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Build query parameters - only include non-empty values
      const queryParams = new URLSearchParams();
      
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });
      
      // Add expandSearch parameter
      queryParams.append('expandSearch', 'true');
      
      // Fetch all pages if needed
      let allOrderIds = [];
      let currentPage = 1;
      const pageSize = 5000;
      let totalPages = 1;
      
      do {
        // Set page and limit for this request
        const pageParams = new URLSearchParams(queryParams);
        pageParams.append('page', currentPage.toString());
        pageParams.append('limit', pageSize.toString());
        
        // Make API request
        const response = await fetch(`${API_URL}/orders?${pageParams.toString()}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch order IDs');
        }
        
        const data = await response.json();
        
        // Extract order IDs from this page
        const pageOrderIds = data.records.map((order, index) => order.id || order['Ord #']); // Use order number as ID
        allOrderIds = [...allOrderIds, ...pageOrderIds];
        
        // Update pagination info
        totalPages = data.pagination.totalPages;
        currentPage++;
        
        // If we have more than 10,000 records, use virtual selection instead
        if (data.pagination.totalRecords > 10000) {
          setVirtualSelectAll(true);
          setVirtualSelectionCount(data.pagination.totalRecords);
          setSelectedOrders(allOrderIds.slice(0, 5000)); // Keep first 5000 as sample
          setAllMatchingOrderIds(allOrderIds);
          setSelectAllInProgress(false);
          return allOrderIds;
        }
        
      } while (currentPage <= totalPages);
      
      // Set all the collected order IDs
      setAllMatchingOrderIds(allOrderIds);
      setVirtualSelectionCount(allOrderIds.length);
      setSelectAllInProgress(false);
      
      return allOrderIds;
    } catch (err) {
      console.error('Error fetching all matching order IDs:', err);
      setSelectAllInProgress(false);
      setWarning('Failed to fetch all matching orders for bulk selection. Selecting only current page.');
      return filteredOrders.map(order => order.id);
    }
  };
  
  // Function to execute search based on criteria
  const executeSearch = async () => {
    try {
      // Validate the business unit search if it's INCOMM with no other criteria
      if (
        searchCriteria.businessUnit.toUpperCase() === 'INCOMM' && 
        !searchCriteria.orderNumber && 
        !searchCriteria.po && 
        !searchCriteria.wave && 
        !searchCriteria.shipDate && 
        !searchCriteria.orderRcvDateFrom && 
        !searchCriteria.orderRcvDateTo && 
        !searchCriteria.dueDateFrom && 
        !searchCriteria.dueDateTo
      ) {
        setWarning(
          "Searching only by Business Unit 'INCOMM' would return over 1 million records. " +
          "Please add additional search criteria to narrow your search."
        );
        return;
      }
      
      setSearchInProgress(true);
      setError(null);
      setWarning(null);
      
      // Apply the search criteria to active filters
      setActiveFilters({...searchCriteria, showLateOnly});
      
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Build query parameters - only include non-empty values
      const queryParams = new URLSearchParams();
      
      Object.entries(searchCriteria).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });
      
      // Add showLateOnly filter if active
      if (showLateOnly) {
        queryParams.append('showLateOnly', 'true');
      }
      
      // Reset to first page when executing a new search
      setCurrentPage(1);
      queryParams.append('page', '1');
      
      // Add limit parameter
      queryParams.append('limit', RECORDS_PER_PAGE.toString());
      
      // Add expandSearch parameter to get all matching records
      queryParams.append('expandSearch', 'true');
      
      // Make API request
      const response = await fetch(`${API_URL}/orders?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }
      
      const data = await response.json();
      
      // Map database fields to component fields with Updated_Due_Date/Corrected_Due_Date handling
      const formattedData = data.records.map((order, index) => ({
        id: order.id || order['Ord #'], // Use order number as ID
        orderNumber: order['Ord #'],
        businessUnit: order['Business Unit'],
        po: order['PO'],
        wave: order['Wave'],
        // Remove shipDate field from the mapped data
        receivedDate: order['Order RCV Date'],
        dueDate: order['Updated_Due_Date'] || order['Corrected_Due_Date'], // Prefer Updated_Due_Date, fall back to Corrected_Due_Date
        orderClosedTStamp: order['Order Closed TStamp'],
        orderStatus: order['Order_Status'],
        inaccurateOrder: order['inaccurate_order'] || 0, // Add accuracy status
      }));
      
      setOrders(formattedData);
      setFilteredOrders(formattedData);
      setCurrentPage(data.pagination.page);
      setTotalPages(data.pagination.totalPages);
      setTotalRecords(data.pagination.totalRecords);
      
      // Reset cached pages and store current page
      const newCachedPages = {
        1: formattedData
      };
      setCachedPages(newCachedPages);
      
      // Clear selection when new search results arrive
      setSelectedOrders([]);
      setVirtualSelectAll(false);
      setVirtualSelectionCount(0);
      
      // Don't set allMatchingOrderIds here - it will be populated when user clicks Select All
      setAllMatchingOrderIds([]);
      
      // Set warning if provided by the API
      if (data.warning) {
        setWarning(data.warning);
      }
      
      // Pre-fetch adjacent pages if needed
      prefetchAdjacentPages(1, newCachedPages, queryParams, token);
      
      setSearchInProgress(false);
    } catch (err) {
      setError(err.message || 'Failed to load orders data');
      setSearchInProgress(false);
    }
  };
  
  // Function to prefetch and cache adjacent pages
  const prefetchAdjacentPages = useCallback(async (currentPage, currentCachedPages, queryParams, token) => {
    // Clone the query parameters
    const params = new URLSearchParams(queryParams.toString());
    
    // Determine which pages to prefetch
    const pagesToFetch = [];
    
    // Add pages before current page
    for (let i = 1; i <= CACHE_PAGES_COUNT; i++) {
      const pageNumber = currentPage - i;
      if (pageNumber > 0 && !currentCachedPages[pageNumber]) {
        pagesToFetch.push(pageNumber);
      }
    }
    
    // Add pages after current page
    for (let i = 1; i <= CACHE_PAGES_COUNT; i++) {
      const pageNumber = currentPage + i;
      if (pageNumber <= totalPages && !currentCachedPages[pageNumber]) {
        pagesToFetch.push(pageNumber);
      }
    }
    
    // Fetch and cache each page
    const newCachedPages = { ...currentCachedPages };
    
    for (const pageNumber of pagesToFetch) {
      try {
        params.set('page', pageNumber.toString());
        
        const response = await fetch(`${API_URL}/orders?${params.toString()}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          const formattedData = data.records.map((order, index) => ({
            id: order.id || order['Ord #'], // Use order number as ID
            orderNumber: order['Ord #'],
            businessUnit: order['Business Unit'],
            po: order['PO'],
            wave: order['Wave'],
            // Remove shipDate field from the mapped data
            receivedDate: order['Order RCV Date'],
            dueDate: order['Updated_Due_Date'] || order['Corrected_Due_Date'],
            orderClosedTStamp: order['Order Closed TStamp'],
            orderStatus: order['Order_Status'],
            inaccurateOrder: order['inaccurate_order'] || 0, // Add accuracy status
          }));
          
          newCachedPages[pageNumber] = formattedData;
        }
      } catch (err) {
        console.error(`Failed to prefetch page ${pageNumber}:`, err);
        // Continue with other pages even if one fails
      }
    }
    
    setCachedPages(newCachedPages);
  }, [API_URL, totalPages]);
  
  // Function to change the page
  const changePage = async (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage) return;
    
    // Check if the page is already cached
    if (cachedPages[newPage]) {
      setFilteredOrders(cachedPages[newPage]);
      setCurrentPage(newPage);
      
      // Prefetch additional pages if needed
      const token = localStorage.getItem('token');
      if (token) {
        // Build query parameters based on active filters
        const queryParams = new URLSearchParams();
        
        Object.entries(activeFilters).forEach(([key, value]) => {
          if (value) {
            // Map the date range fields to the API expected format
            if (key === 'receivedDateFrom' || key === 'receivedDateTo') {
              queryParams.append(key.replace('received', 'orderRcv'), value);
            } else {
              queryParams.append(key, value);
            }
          }
        });
        
        queryParams.append('limit', RECORDS_PER_PAGE.toString());

        if (hasActiveFilters()) {
          queryParams.append('expandSearch', 'true');
        }
        
        prefetchAdjacentPages(newPage, cachedPages, queryParams, token);
      }
      
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Build query parameters based on active filters
      const queryParams = new URLSearchParams();
      
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });
      
      // Add page and limit parameters
      queryParams.append('page', newPage.toString());
      queryParams.append('limit', RECORDS_PER_PAGE.toString());

      // Add expandSearch if any filters are active
      if (hasActiveFilters()) {
        queryParams.append('expandSearch', 'true');
      }
      
      // Make API request
      const response = await fetch(`${API_URL}/orders?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }
      
      const data = await response.json();
      
      // Map database fields to component fields with Updated_Due_Date handling
      const formattedData = data.records.map((order, index) => ({
        id: order.id || order['Ord #'], // Use order number as ID
        orderNumber: order['Ord #'],
        businessUnit: order['Business Unit'],
        po: order['PO'],
        wave: order['Wave'],
        // Remove shipDate field from the mapped data
        receivedDate: order['Order RCV Date'],
        dueDate: order['Updated_Due_Date'] || order['Corrected_Due_Date'], // Prefer Updated_Due_Date, fall back to Corrected_Due_Date
        orderClosedTStamp: order['Order Closed TStamp'],
        orderStatus: order['Order_Status'],
        inaccurateOrder: order['inaccurate_order'] || 0, // Add accuracy status
      }));
      
      // Update the state
      setOrders(formattedData);
      setFilteredOrders(formattedData);
      setCurrentPage(data.pagination.page);
      setTotalPages(data.pagination.totalPages);
      setTotalRecords(data.pagination.totalRecords);
      
      // Add to cached pages
      const newCachedPages = {
        ...cachedPages,
        [newPage]: formattedData
      };
      setCachedPages(newCachedPages);
      
      // Prefetch adjacent pages
      prefetchAdjacentPages(newPage, newCachedPages, queryParams, token);
      
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load orders data');
      setLoading(false);
    }
  };
  
  // Handle search criteria change
  const handleSearchCriteriaChange = (e) => {
    const { name, value } = e.target;
    setSearchCriteria(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear any warnings when criteria change
    setWarning(null);
  };
  
  // Clear all search criteria
  const clearSearchCriteria = () => {
    setSearchCriteria({
      orderNumber: '',
      businessUnit: '',
      po: '',
      wave: '',
      shipDate: '',
      orderRcvDateFrom: '',
      orderRcvDateTo: '',
      dueDateFrom: '',
      dueDateTo: ''
    });
    setShowLateOnly(false);
    setWarning(null);
  };
  
  // Toggle selection of a single order - completely rewritten to fix selection issues
  const toggleOrderSelection = (orderId, event) => {
    // Prevent default browser behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // If we're in virtual select all mode, turning off one item should exit that mode
    if (virtualSelectAll) {
      setVirtualSelectAll(false);
      // Get all visible items except the one being deselected
      const visibleIds = filteredOrders.map(order => order.id).filter(id => id !== orderId);
      setSelectedOrders(visibleIds);
      setVirtualSelectionCount(visibleIds.length);
      return;
    }
    
    // Check maximum selection limit
    if (selectedOrders.length >= MAX_SELECTABLE_ORDERS && !selectedOrders.includes(orderId)) {
      setWarning(`Cannot select more than ${MAX_SELECTABLE_ORDERS} orders for bulk update.`);
      return;
    }
    
    // Toggle only this specific order's selection state
    setSelectedOrders(prevSelected => {
      // Check if this order is already selected
      const isCurrentlySelected = prevSelected.includes(orderId);
      
      if (isCurrentlySelected) {
        // If selected, remove it by filtering it out
        console.log(`Deselecting order ID: ${orderId}`);
        return prevSelected.filter(id => id !== orderId);
      } else {
        // If not selected, add it to selection
        console.log(`Selecting order ID: ${orderId}`);
        return [...prevSelected, orderId];
      }
    });
  };
  
  // Select all orders that match current filters
  const selectAllOrders = async () => {
    // Only allow select all when filters are active
    if (hasActiveFilters()) {
      // If we're already in virtual select all mode, toggle it off
      if (virtualSelectAll) {
        setVirtualSelectAll(false);
        setSelectedOrders([]);
        setVirtualSelectionCount(0);
        return;
      }
      
      // Otherwise, fetch all IDs and initiate virtual select all
      const matchingCount = await fetchAllMatchingOrderIds();
      
      // If too many records to store all IDs in memory, use virtual selection 
      if (matchingCount.length >= 10000 || totalRecords > 10000) {
        setVirtualSelectAll(true);
        setVirtualSelectionCount(totalRecords);
        
        // Store as many IDs as feasible, but rely on the API to process all matching records
        setSelectedOrders(matchingCount.slice(0, 5000)); // Just keep a representative sample
      } else {
        // Otherwise, we can store all IDs
        setSelectedOrders(matchingCount);
        setVirtualSelectionCount(matchingCount.length);
      }
    }
    // If no filters are active, the button should be disabled, so this shouldn't be reached
  };
  
  // Open edit modal for selected orders
  const openEditModal = () => {
    if (selectedOrders.length === 0 && !virtualSelectAll) {
      alert("Please select at least one order to update");
      return;
    }
    
    // If only one order is selected, pre-fill with its due date
    if (selectedOrders.length === 1 && !virtualSelectAll) {
      const order = orders.find(o => o.id === selectedOrders[0]);
      if (order) {
        setNewDueDate(order.dueDate || '');
      }
    } else {
      setNewDueDate('');
    }
    
    setIsEditModalOpen(true);
  };
  
  // Close edit modal
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setNewDueDate('');
  };
  
  // Open accuracy modal
  const openAccuracyModal = () => {
    if (selectedOrders.length === 0 && !virtualSelectAll) {
      setWarning("Please select at least one order to update accuracy");
      return;
    }
    setIsAccuracyModalOpen(true);
  };
  
  // Close accuracy modal
  const closeAccuracyModal = () => {
    setIsAccuracyModalOpen(false);
  };
  
  // Update accuracy for selected orders
  const updateAccuracy = async (isAccurate) => {
    if ((selectedOrders.length === 0 && !virtualSelectAll)) return;
    
    try {
      setUpdateInProgress(true);
      
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Determine the total number of records to update
      const totalToUpdate = virtualSelectAll ? virtualSelectionCount : selectedOrders.length;
      setBatchUpdateStatus({ current: 0, total: totalToUpdate });
      
      let response;
      
      if (virtualSelectAll) {
        // For virtual selection, use the filter-based approach
        response = await fetch(`${API_URL}/orders/update-accuracy-by-filter`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            filters: activeFilters,
            isAccurate: isAccurate
          })
        });
      } else {
        // For direct order selection
        const selectedOrderNumbers = selectedOrders.filter(Boolean);
        
        response = await fetch(`${API_URL}/orders/update-accuracy-by-ids`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            orderNumbers: selectedOrderNumbers,
            isAccurate: isAccurate
          })
        });
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update accuracy: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Update progress indicator
      setBatchUpdateStatus({
        current: data.updatedCount || totalToUpdate,
        total: data.updatedCount || totalToUpdate
      });
      
      // Show success message
      setWarning(`Successfully marked ${data.updatedCount || totalToUpdate} orders as ${isAccurate ? 'accurate' : 'inaccurate'}.`);
      
      // Refresh orders to show updates
      await refreshOrdersWithCurrentFilters();
      
      // Reset selection state
      setSelectedOrders([]);
      setVirtualSelectAll(false);
      setVirtualSelectionCount(0);
      closeAccuracyModal();
      
    } catch (err) {
      console.error('Update Accuracy Error:', err);
      setError(err.message || 'Failed to update accuracy');
    } finally {
      setUpdateInProgress(false);
      setBatchUpdateStatus({ current: 0, total: 0 });
    }
  };
  
  // Update due date for selected orders in batches
 // Update due date for selected orders
const updateDueDate = async () => {
  if ((selectedOrders.length === 0 && !virtualSelectAll) || !newDueDate) return;
  
  try {
    setUpdateInProgress(true);
    
    // Determine the total number of records to update for the progress bar
    const totalToUpdate = virtualSelectAll ? virtualSelectionCount : selectedOrders.length;
    setBatchUpdateStatus({ current: 0, total: totalToUpdate });
    
    // Get token
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    
    if (virtualSelectAll) {
      // For virtual selection, use the filter-based approach (keep this the same)
      const response = await fetch(`${API_URL}/orders/update-due-dates-by-filter`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          filters: activeFilters,
          newDueDate: newDueDate
        })
      });
      
      if (!response.ok) {
        // Parse error response if possible
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error Response:', errorData);
        throw new Error(errorData.message || `Failed to update due dates: ${response.status} ${response.statusText}`);
      }
      
      // Get response data
      const data = await response.json();
      console.log('Update response:', data);
      
      // Update progress indicator to reflect actual number of updated records
      setBatchUpdateStatus({
        current: data.updatedCount || totalToUpdate,
        total: data.updatedCount || totalToUpdate
      });
    } else {
      // For direct order selection, use the new dedicated endpoint
      
      // Extract order numbers from the selected orders
      // Since we're now using order numbers as IDs, we can use them directly
      const selectedOrderNumbers = selectedOrders.filter(Boolean);
      
      // Use the new endpoint
      const response = await fetch(`${API_URL}/orders/update-orders-by-ids`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderNumbers: selectedOrderNumbers,
          newDueDate: newDueDate
        })
      });
      
      if (!response.ok) {
        // Parse error response if possible
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error Response:', errorData);
        throw new Error(errorData.message || `Failed to update due dates: ${response.status} ${response.statusText}`);
      }
      
      // Get response data
      const data = await response.json();
      console.log('Update response:', data);
      
      // Update progress indicator to reflect actual number of updated records
      setBatchUpdateStatus({
        current: data.updatedCount || selectedOrders.length,
        total: data.updatedCount || selectedOrders.length
      });
    }
    
    // Show success message
    setWarning(`Successfully updated due dates for the selected orders.`);
    
    // Refresh orders to show updates with current filters
    await refreshOrdersWithCurrentFilters();
    
    // Reset selection state
    setSelectedOrders([]);
    setVirtualSelectAll(false);
    setVirtualSelectionCount(0);
    closeEditModal();
    
  } catch (err) {
    console.error('Update Error:', err);
    setError(err.message || 'Failed to update due dates');
  } finally {
    setUpdateInProgress(false);
    setBatchUpdateStatus({ current: 0, total: 0 });
  }
};
  
  // Refresh data using current active filters
  const refreshOrdersWithCurrentFilters = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Build query parameters based on active filters
      const queryParams = new URLSearchParams();
      
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });
      
      // Add pagination parameters
      queryParams.append('page', currentPage.toString());
      queryParams.append('limit', RECORDS_PER_PAGE.toString());

      // Add expandSearch if any filters are active
      if (hasActiveFilters()) {
        queryParams.append('expandSearch', 'true');
      }
      
      // Make API request
      const response = await fetch(`${API_URL}/orders?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }
      
      const data = await response.json();
      
      // Map database fields to component fields with Updated_Due_Date handling
      const formattedData = data.records.map((order, index) => ({
        id: order.id || order['Ord #'], // Use order number as ID
        orderNumber: order['Ord #'],
        businessUnit: order['Business Unit'],
        po: order['PO'],
        wave: order['Wave'],
        // Remove shipDate field from the mapped data
        receivedDate: order['Order RCV Date'],
        dueDate: order['Updated_Due_Date'] || order['Corrected_Due_Date'], // Prefer Updated_Due_Date, fall back to Corrected_Due_Date
        orderClosedTStamp: order['Order Closed TStamp'],
        orderStatus: order['Order_Status'],
        inaccurateOrder: order['inaccurate_order'] || 0, // Add accuracy status
      }));
      
      setOrders(formattedData);
      setFilteredOrders(formattedData);
      setCurrentPage(data.pagination.page);
      setTotalPages(data.pagination.totalPages);
      setTotalRecords(data.pagination.totalRecords);
      
      // Update cached pages
      const newCachedPages = {
        ...cachedPages,
        [currentPage]: formattedData
      };
      setCachedPages(newCachedPages);
      
      // Set warning if provided by the API
      if (data.warning) {
        setWarning(data.warning);
      }
      
      setLoading(false);
      
      // Prefetch adjacent pages if needed
      prefetchAdjacentPages(currentPage, newCachedPages, queryParams, token);
      
    } catch (err) {
      setError(err.message || 'Failed to refresh orders data');
      setLoading(false);
    }
  };
  
  // Refresh data completely - reset filters and reload initial data
  const refreshData = () => {
    fetchTotalCount();
    loadInitialOrders();
    setSelectedOrders([]);
    setVirtualSelectAll(false);
    setVirtualSelectionCount(0);
    setActiveFilters({
      orderNumber: '',
      businessUnit: '',
      po: '',
      wave: '',
      shipDate: '',
      orderRcvDateFrom: '',
      orderRcvDateTo: '',
      dueDateFrom: '',
      dueDateTo: '',
      showLateOnly: false
    });
    setSearchCriteria({
      orderNumber: '',
      businessUnit: '',
      po: '',
      wave: '',
      shipDate: '',
      orderRcvDateFrom: '',
      orderRcvDateTo: '',
      dueDateFrom: '',
      dueDateTo: ''
    });
    setShowLateOnly(false);
    setWarning(null);
    setCachedPages({});
  };

  // Export filtered orders to CSV
  const exportToCSV = async () => {
    // Check if filters are active
    if (!hasActiveFilters()) {
      setWarning('Please apply search filters before exporting');
      return;
    }

    try {
      setExportInProgress(true);
      setError(null);

      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // Build query parameters based on active filters
      const queryParams = new URLSearchParams();

      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });

      // Add expandSearch parameter to get all matching records
      queryParams.append('expandSearch', 'true');

      // Fetch all pages
      let allOrders = [];
      let currentPage = 1;
      let totalPages = 1;
      const pageSize = 5000; // Use larger page size for export

      do {
        // Set page and limit for this request
        const pageParams = new URLSearchParams(queryParams);
        pageParams.append('page', currentPage.toString());
        pageParams.append('limit', pageSize.toString());

        // Make API request
        const response = await fetch(`${API_URL}/orders?${pageParams.toString()}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch orders for export');
        }

        const data = await response.json();

        // Map and add orders from this page
        const formattedData = data.records.map((order) => ({
          orderNumber: order['Ord #'],
          businessUnit: order['Business Unit'],
          po: order['PO'],
          wave: order['Wave'],
          receivedDate: order['Order RCV Date'],
          dueDate: order['Updated_Due_Date'] || order['Corrected_Due_Date'],
          orderClosedTStamp: order['Order Closed TStamp'],
          inaccurateOrder: order['inaccurate_order'] || 0,
          orderStatus: order['Order_Status']
        }));

        allOrders = [...allOrders, ...formattedData];

        // Update pagination info
        totalPages = data.pagination.totalPages;
        currentPage++;

      } while (currentPage <= totalPages);

      // Convert to CSV
      if (allOrders.length === 0) {
        setWarning('No orders found to export');
        setExportInProgress(false);
        return;
      }

      // CSV Headers
      const headers = [
        'Order #',
        'Business Unit',
        'PO',
        'Wave',
        'Order RCV Date',
        'Due Date',
        'Order Close',
        'Accurate',
        'Status'
      ];

      // CSV Rows
      const csvRows = [
        headers.join(','),
        ...allOrders.map(order => [
          `"${order.orderNumber || ''}"`,
          `"${order.businessUnit || ''}"`,
          `"${order.po || ''}"`,
          `"${order.wave || ''}"`,
          `"${formatDate(order.receivedDate) || ''}"`,
          `"${formatDate(order.dueDate) || ''}"`,
          `"${formatDate(order.orderClosedTStamp) || ''}"`,
          `"${order.inaccurateOrder === 1 ? 'No' : 'Yes'}"`,
          `"${order.orderStatus || ''}"`,
        ].join(','))
      ];

      const csvContent = csvRows.join('\n');

      // Create blob and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `orders_export_${timestamp}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setWarning(`Successfully exported ${allOrders.length} orders to ${filename}`);

    } catch (err) {
      console.error('Export Error:', err);
      setError(err.message || 'Failed to export orders');
    } finally {
      setExportInProgress(false);
    }
  };

// Much simpler fix for the formatDate function
const formatDate = (dateString) => {
  if (!dateString) return '';
  
  // Split by any non-digit characters to get [year, month, day]
  const parts = dateString.split(/\D+/).filter(part => part);
  
  // Only proceed if we have enough parts
  if (parts.length >= 3) {
    // Create date parts manually - no timezone interpretation
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
    const day = parseInt(parts[2], 10);
    
    // Format directly with parts
    const date = new Date(year, month, day);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  }
  
  // Fallback to original string if parsing fails
  return dateString;
};
  
  // Check if there are any active filters applied
  const hasActiveFilters = () => {
    return Object.entries(activeFilters).some(([key, value]) => {
      if (key === 'showLateOnly') return value === true;
      return value !== '';
    });
  };

  // Get the effective selection count for display
  const getSelectionCount = () => {
    if (virtualSelectAll) {
      return virtualSelectionCount;
    }
    return selectedOrders.length;
  };
  
  // Check if an order should appear selected (either actually selected or virtual selection)
  const isOrderSelected = (orderId) => {
    // If we're in virtual select all mode for a filtered search, all visible orders should appear selected
    if (virtualSelectAll) {
      return true;
    }
    
    // Otherwise use the actual selection state
    return selectedOrders.includes(orderId);
  };
  
  // Calculate if any operation is in progress for the overlay
  const isOperationInProgress = loading || searchInProgress || updateInProgress || selectAllInProgress || exportInProgress;
  
  // Pagination Controls Component
  const PaginationControls = () => (
    totalPages > 1 ? (
      <div className="flex items-center space-x-2">
        <button
          onClick={() => changePage(1)}
          disabled={currentPage === 1 || isOperationInProgress}
          className={`p-1 rounded ${currentPage === 1 ? 'text-slate-600' : 'text-slate-400 hover:text-slate-300'}`}
          aria-label="First page"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
        
        <button
          onClick={() => changePage(currentPage - 1)}
          disabled={currentPage === 1 || isOperationInProgress}
          className={`p-1 rounded ${currentPage === 1 ? 'text-slate-600' : 'text-slate-400 hover:text-slate-300'}`}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        
        <span className="text-xs text-slate-400 px-2">
          Page {currentPage} of {totalPages}
        </span>
        
        <button
          onClick={() => changePage(currentPage + 1)}
          disabled={currentPage === totalPages || isOperationInProgress}
          className={`p-1 rounded ${currentPage === totalPages ? 'text-slate-600' : 'text-slate-400 hover:text-slate-300'}`}
          aria-label="Next page"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
        
        <button
          onClick={() => changePage(totalPages)}
          disabled={currentPage === totalPages || isOperationInProgress}
          className={`p-1 rounded ${currentPage === totalPages ? 'text-slate-600' : 'text-slate-400 hover:text-slate-300'}`}
          aria-label="Last page"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    ) : null
  );
  
  return (
    <div className="w-full h-full flex flex-col relative">
      {/* Loading Overlay - displayed during any operation - explicitly set highest z-index */}
      {isOperationInProgress && (
        <div className="absolute inset-0 bg-slate-900/80 z-[9999] flex flex-col items-center justify-center">
          <svg className="animate-spin h-10 w-10 text-blue-500 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          
          {loading && <p className="text-blue-200 text-lg font-medium">Loading orders...</p>}
          {searchInProgress && <p className="text-blue-200 text-lg font-medium">Searching orders...</p>}
          {selectAllInProgress && <p className="text-blue-200 text-lg font-medium">Retrieving all matching orders...</p>}
          {exportInProgress && <p className="text-blue-200 text-lg font-medium">Exporting data to CSV...</p>}

          {updateInProgress && (
            <>
              <p className="text-blue-200 text-lg font-medium mb-2">Updating due dates...</p>
              <div className="w-64 bg-slate-700 rounded-full h-3 mb-2 overflow-hidden">
                <div 
                  className="bg-blue-500 h-full rounded-full transition-all duration-300 ease-in-out"
                  style={{ width: `${(batchUpdateStatus.current / batchUpdateStatus.total) * 100}%` }}
                ></div>
              </div>
              <p className="text-blue-200 text-sm">
                {batchUpdateStatus.current} of {batchUpdateStatus.total} orders updated
              </p>
            </>
          )}
        </div>
      )}
      
      {/* Header with title and actions - moved Clear and Search buttons here */}
      <div className="p-4 border-b border-slate-700 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-slate-200">Update Order Due Date & Accuracy</h2>
          
          <div className="flex flex-wrap gap-2">
            {/* Clear and Search buttons moved here */}
            <button 
              onClick={clearSearchCriteria}
              className="text-xs px-2 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-slate-300 rounded"
              disabled={isOperationInProgress}
            >
              <X className="w-3 h-3 inline-block mr-1" />
              Clear
            </button>
            
            <button
              onClick={executeSearch}
              className="flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 
                      text-xs text-white rounded transition-colors"
              disabled={isOperationInProgress}
            >
              <Search className="w-3 h-3 mr-1" />
              Search
            </button>
            
            <button
              onClick={() => setShowLateOnly(!showLateOnly)}
              className={`flex items-center px-3 py-1.5 rounded transition-colors text-xs
                      ${showLateOnly 
                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                        : 'bg-slate-700 hover:bg-slate-600 text-slate-300'}`}
              disabled={isOperationInProgress}
            >
              <Filter className="w-3 h-3 mr-1" />
              {showLateOnly ? 'Hide Late' : 'Show Late'}
            </button>

            <button
              onClick={selectAllOrders}
              className={`flex items-center px-3 py-1.5 rounded-md transition-colors text-sm
                      ${hasActiveFilters()
                        ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                        : 'bg-slate-800 text-slate-500 cursor-not-allowed'}`}
              disabled={filteredOrders.length === 0 || isOperationInProgress || !hasActiveFilters()}
            >
              {/* Button text based on selection state */}
              {hasActiveFilters() ? (
                // When filters are active
                virtualSelectAll || (selectedOrders.length > 0 && selectedOrders.length >= totalRecords) ? 
                  <span className="flex items-center">
                    <CheckSquare className="w-4 h-4 mr-1" />
                    Deselect All Results
                  </span> :
                  <span className="flex items-center">
                    <Square className="w-4 h-4 mr-1" />
                    Select All Results
                  </span>
              ) : (
                // When no filters are active (button is disabled)
                <span className="flex items-center">
                  <Square className="w-4 h-4 mr-1" />
                  Select All Results
                </span>
              )}
            </button>
            
            <button 
              onClick={openAccuracyModal}
              className="flex items-center px-3 py-1.5 bg-green-600 hover:bg-green-700 
                      text-white rounded-md transition-colors text-sm"
              disabled={(selectedOrders.length === 0 && !virtualSelectAll) || isOperationInProgress}
            >
              <CheckSquare className="w-4 h-4 mr-1" />
              Update Accuracy ({getSelectionCount()})
            </button>
            
            <button
              onClick={openEditModal}
              className="flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700
                      text-white rounded-md transition-colors text-sm"
              disabled={(selectedOrders.length === 0 && !virtualSelectAll) || isOperationInProgress}
            >
              <Calendar className="w-4 h-4 mr-1" />
              Update Due Date ({getSelectionCount()})
            </button>

            <button
              onClick={exportToCSV}
              className={`flex items-center px-3 py-1.5 rounded-md transition-colors text-sm
                      ${hasActiveFilters()
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-slate-800 text-slate-500 cursor-not-allowed'}`}
              disabled={!hasActiveFilters() || isOperationInProgress}
            >
              <Download className="w-4 h-4 mr-1" />
              Export CSV
            </button>

            <button
              onClick={refreshData}
              className="flex items-center px-3 py-1.5 bg-slate-700 hover:bg-slate-600
                      text-slate-300 rounded-md transition-colors text-sm"
              disabled={isOperationInProgress}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </button>
          </div>
        </div>
      </div>
      
      {/* Warning banner for large results */}
      {warning && (
        <div className="mx-4 mt-2 p-2 bg-yellow-700/40 border border-yellow-600 rounded flex items-start">
          <AlertTriangle className="w-5 h-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-yellow-300 text-sm">{warning}</p>
        </div>
      )}
      
      {/* Error banner */}
      {error && (
        <div className="mx-4 mt-2 p-2 bg-red-700/40 border border-red-600 rounded flex items-start">
          <AlertCircle className="w-5 h-5 text-red-400 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}
      
      {/* Search section - now without Clear and Search buttons */}
      <div className="p-4 border-b border-slate-700 bg-slate-700/20 flex-shrink-0">
        {/* Search fields - organized into logical groups */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {/* Order Information Group */}
          <div className="bg-slate-800/30 rounded-md p-3">
            <h3 className="text-xs uppercase text-slate-500 mb-2">Order Information</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <label htmlFor="orderNumber" className="block text-xs text-slate-400 mb-1">Order #</label>
                <input
                  id="orderNumber"
                  type="text"
                  name="orderNumber"
                  value={searchCriteria.orderNumber}
                  onChange={handleSearchCriteriaChange}
                  placeholder="Order Number"
                  className="w-full pl-2 pr-6 py-1.5 bg-slate-700 border border-slate-600 rounded-md text-sm
                          text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 
                          focus:ring-blue-500"
                  disabled={isOperationInProgress}
                />
              </div>
              
              <div className="relative">
                <label htmlFor="businessUnit" className="block text-xs text-slate-400 mb-1">Business Unit</label>
                {businessUnits.length > 0 ? (
                  <select
                    id="businessUnit"
                    name="businessUnit"
                    value={searchCriteria.businessUnit}
                    onChange={handleSearchCriteriaChange}
                    className="w-full pl-2 pr-6 py-1.5 bg-slate-700 border border-slate-600 rounded-md text-sm
                            text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 
                            focus:ring-blue-500 appearance-none"
                    disabled={isOperationInProgress || loadingBusinessUnits}
                  >
                    <option value="" className="bg-slate-800">Select Business Unit</option>
                    {businessUnits.map(unit => (
                      <option key={unit.id} value={unit.identifier} className="bg-slate-800">
                        {unit.displayName}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="businessUnit"
                    type="text"
                    name="businessUnit"
                    value={searchCriteria.businessUnit}
                    onChange={handleSearchCriteriaChange}
                    placeholder="Business Unit"
                    className="w-full pl-2 pr-6 py-1.5 bg-slate-700 border border-slate-600 rounded-md text-sm
                            text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 
                            focus:ring-blue-500"
                    disabled={isOperationInProgress || loadingBusinessUnits}
                  />
                )}
              </div>
              
              <div className="relative">
                <label htmlFor="po" className="block text-xs text-slate-400 mb-1">PO</label>
                <input
                  id="po"
                  type="text"
                  name="po"
                  value={searchCriteria.po}
                  onChange={handleSearchCriteriaChange}
                  placeholder="Purchase Order"
                  className="w-full pl-2 pr-6 py-1.5 bg-slate-700 border border-slate-600 rounded-md text-sm
                          text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 
                          focus:ring-blue-500"
                  disabled={isOperationInProgress}
                />
              </div>
              
              <div className="relative">
                <label htmlFor="wave" className="block text-xs text-slate-400 mb-1">Wave</label>
                <input
                  id="wave"
                  type="text"
                  name="wave"
                  value={searchCriteria.wave}
                  onChange={handleSearchCriteriaChange}
                  placeholder="Wave"
                  className="w-full pl-2 pr-6 py-1.5 bg-slate-700 border border-slate-600 rounded-md text-sm
                          text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 
                          focus:ring-blue-500"
                  disabled={isOperationInProgress}
                />
              </div>
            </div>
          </div>
          
          {/* Dates Group */}
          <div className="bg-slate-800/30 rounded-md p-3">
            <h3 className="text-xs uppercase text-slate-500 mb-2">Date Ranges</h3>
            <div className="space-y-3">
              {/* RCV Date Range */}
              <div>
                <label className="block text-xs text-slate-400 mb-1">RCV Date Range</label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <input
                      id="orderRcvDateFrom"
                      type="date"
                      name="orderRcvDateFrom"
                      value={searchCriteria.orderRcvDateFrom}
                      onChange={handleSearchCriteriaChange}
                      className="w-full pl-3 pr-8 py-1.5 bg-slate-700 border border-slate-600 rounded-md text-sm
                              text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 
                              focus:ring-blue-500"
                      disabled={isOperationInProgress}
                      placeholder="From"
                    />
                    <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                  <div className="relative">
                    <input
                      id="orderRcvDateTo"
                      type="date"
                      name="orderRcvDateTo"
                      value={searchCriteria.orderRcvDateTo}
                      onChange={handleSearchCriteriaChange}
                      className="w-full pl-3 pr-8 py-1.5 bg-slate-700 border border-slate-600 rounded-md text-sm
                              text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 
                              focus:ring-blue-500"
                      disabled={isOperationInProgress}
                      placeholder="To"
                    />
                    <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>
              
              {/* Due Date Range */}
              <div>
                <label className="block text-xs text-slate-400 mb-1">Due Date Range</label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <input
                      id="dueDateFrom"
                      type="date"
                      name="dueDateFrom"
                      value={searchCriteria.dueDateFrom}
                      onChange={handleSearchCriteriaChange}
                      className="w-full pl-3 pr-8 py-1.5 bg-slate-700 border border-slate-600 rounded-md text-sm
                              text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 
                              focus:ring-blue-500"
                      disabled={isOperationInProgress}
                      placeholder="From"
                    />
                    <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                  <div className="relative">
                    <input
                      id="dueDateTo"
                      type="date"
                      name="dueDateTo"
                      value={searchCriteria.dueDateTo}
                      onChange={handleSearchCriteriaChange}
                      className="w-full pl-3 pr-8 py-1.5 bg-slate-700 border border-slate-600 rounded-md text-sm
                              text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 
                              focus:ring-blue-500"
                      disabled={isOperationInProgress}
                      placeholder="To"
                    />
                    <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Pagination controls moved here under the Ship Date section */}
        <div className="flex justify-center mt-4">
          <PaginationControls />
        </div>
      </div>
      
      {/* Orders table - scrollable area */}
      <div className="flex-grow overflow-y-auto relative" style={{ height: 'calc(100% - 220px)' }}>
        {/* Error state */}
        {!isOperationInProgress && error && (
          <div className="h-full flex justify-center items-center p-4">
            <div className="bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-md max-w-lg">
              <div className="flex items-center mb-2">
                <AlertCircle className="w-5 h-5 mr-2 text-red-400" />
                <p className="font-medium">Error</p>
              </div>
              <p>{error}</p>
              <button
                onClick={refreshData}
                className="mt-3 text-sm bg-red-800 hover:bg-red-700 px-3 py-1 rounded-md flex items-center w-fit"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Try Again
              </button>
            </div>
          </div>
        )}
        
        {/* Orders table */}
        {!isOperationInProgress && !error && (
          <table className="w-full text-left">
            <thead className="bg-slate-700/50 text-slate-300 text-xs uppercase sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 whitespace-nowrap">Select</th>
                <th className="px-4 py-3 whitespace-nowrap">Ord #</th>
                <th className="px-4 py-3 whitespace-nowrap">Business Unit</th>
                <th className="px-4 py-3 whitespace-nowrap">PO</th>
                <th className="px-4 py-3 whitespace-nowrap">Wave</th>
                <th className="px-4 py-3 whitespace-nowrap">Order RCV Date</th>
                <th className="px-4 py-3 whitespace-nowrap">Due Date</th>
                <th className="px-4 py-3 whitespace-nowrap">Order Close</th>
                <th className="px-4 py-3 whitespace-nowrap">Accurate</th>
                <th className="px-4 py-3 whitespace-nowrap">Status</th>
                {/* Last Updated column removed */}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => {
                  const isSelected = isOrderSelected(order.id);
                  return (
                    <tr 
                      key={order.id} 
                      className={`text-sm ${isSelected ? 'bg-blue-700/30' : ''} cursor-pointer hover:bg-slate-700/30`}
                      onClick={(e) => toggleOrderSelection(order.id, e)}
                    >
                      {/* Checkbox cell - still has its own click handler for clarity */}
                      <td 
                        className="px-4 py-2 whitespace-nowrap text-slate-300"
                        onClick={(e) => {
                          // Stop propagation to prevent double-toggling
                          e.stopPropagation();
                          toggleOrderSelection(order.id, e);
                        }}
                      >
                        <div className="flex justify-center">
                          <button 
                            type="button"
                            className="focus:outline-none"
                            aria-label={isSelected ? "Deselect row" : "Select row"}
                          >
                            {isSelected ? (
                              <CheckSquare className="w-4 h-4 text-blue-400" />
                            ) : (
                              <Square className="w-4 h-4 text-slate-400 hover:text-slate-300" />
                            )}
                          </button>
                        </div>
                      </td>
                      {/* Data cells - no specific click handlers needed now */}
                      <td className="px-4 py-2 whitespace-nowrap text-slate-300">
                        {order.orderNumber}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-slate-300">
                        {order.businessUnit}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-slate-300">
                        {order.po}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-slate-300">
                        {order.wave}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-slate-300">
                        {formatDate(order.receivedDate)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-slate-300">
                        {formatDate(order.dueDate)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-slate-300">
                        {formatDate(order.orderClosedTStamp)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-slate-300">
                        <span className={order.inaccurateOrder === 1 ? "text-red-400" : "text-green-400"}>
                          {order.inaccurateOrder === 1 ? "No" : "Yes"}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-slate-300">
                        <span className={order.orderStatus === 'Late' ? "text-red-400" : "text-slate-300"}>
                          {order.orderStatus || '-'}
                        </span>
                      </td>
                      {/* Last Updated cell removed */}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className="px-4 py-6 text-center text-slate-400">
                    No orders found matching your search criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        
        {/* Record Count display - pagination removed from here */}
        {!isOperationInProgress && !error && (
          <div className="p-3 border-t border-slate-700 bg-slate-800">
            <div className="flex justify-between items-center">
              <p className="text-xs text-slate-400">
                Showing {filteredOrders.length} of {totalRecords} orders 
                {totalRecords > 0 && ` (Page ${currentPage} of ${totalPages})`}
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Edit Due Date Modal - more compact */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg shadow-lg max-w-md w-full border border-slate-700">
            <div className="p-4 border-b border-slate-700 flex justify-between items-center">
              <h3 className="text-lg font-medium text-slate-200">Update Due Date</h3>
              <button 
                onClick={closeEditModal}
                className="text-slate-400 hover:text-slate-300"
                disabled={updateInProgress}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <p className="text-sm text-slate-400 mb-2">Selected Orders:</p>
                {!virtualSelectAll && selectedOrders.length === 1 ? (
                  <>
                    {orders.filter(o => o.id === selectedOrders[0]).map(order => (
                      <div key={order.id} className="text-sm">
                        <p className="text-slate-300 mb-1"><span className="text-slate-400">Order:</span> {order.orderNumber}</p>
                        <p className="text-slate-300 mb-1"><span className="text-slate-400">Business Unit:</span> {order.businessUnit}</p>
                        <p className="text-slate-300 mb-3"><span className="text-slate-400">Current Due Date:</span> {formatDate(order.dueDate)}</p>
                      </div>
                    ))}
                  </> 
                ) : (
                  <p className="text-slate-300 mb-3">
                    Updating due date for <span className="font-semibold">{getSelectionCount()}</span> orders
                  </p>
                )}
                
                {getSelectionCount() > 1000 && (
                  <div className="mb-3 p-2 bg-yellow-900/30 border border-yellow-800 rounded-md text-yellow-300 text-xs">
                    <AlertCircle className="inline-block w-3 h-3 mb-0.5 mr-1" />
                    You are about to update {getSelectionCount()} records. This operation will be processed in batches and may take some time.
                  </div>
                )}
              </div>
              
              <div className="mb-4">
                <label htmlFor="newDueDate" className="block text-sm font-medium text-slate-300 mb-1">
                  New Due Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="newDueDate"
                    value={newDueDate}
                    onChange={(e) => setNewDueDate(e.target.value)}
                    className="w-full pl-3 pr-8 py-1.5 bg-slate-700 border border-slate-600 rounded-md
                             text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    disabled={updateInProgress}
                  />
                  <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <button
                  onClick={closeEditModal}
                  className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-md transition-colors text-sm"
                  disabled={updateInProgress}
                >
                  Cancel
                </button>
                <button
                  onClick={updateDueDate}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-sm"
                  disabled={!newDueDate || updateInProgress}
                >
                  Update {getSelectionCount() > 1 ? `(${getSelectionCount()})` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Accuracy Update Modal */}
      {isAccuracyModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg shadow-lg max-w-md w-full border border-slate-700">
            <div className="p-4 border-b border-slate-700 flex justify-between items-center">
              <h3 className="text-lg font-medium text-slate-200">Update Accuracy</h3>
              <button 
                onClick={closeAccuracyModal}
                className="text-slate-400 hover:text-slate-300"
                disabled={updateInProgress}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <p className="text-sm text-slate-400 mb-2">Selected Orders:</p>
                {!virtualSelectAll && selectedOrders.length === 1 ? (
                  <>
                    {orders.filter(o => o.id === selectedOrders[0]).map(order => (
                      <div key={order.id} className="text-sm">
                        <p className="text-slate-300 mb-1"><span className="text-slate-400">Order:</span> {order.orderNumber}</p>
                        <p className="text-slate-300 mb-1"><span className="text-slate-400">Business Unit:</span> {order.businessUnit}</p>
                        <p className="text-slate-300 mb-3"><span className="text-slate-400">Due Date:</span> {formatDate(order.dueDate)}</p>
                      </div>
                    ))}
                  </> 
                ) : (
                  <p className="text-slate-300 mb-3">
                    You have selected <span className="font-semibold">{getSelectionCount()}</span> orders
                  </p>
                )}
                
                <p className="text-slate-300 mb-4">
                  Would you like to mark these orders as accurate or inaccurate?
                </p>
              </div>
              
              <div className="flex justify-end gap-2">
                <button
                  onClick={closeAccuracyModal}
                  className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-md transition-colors text-sm"
                  disabled={updateInProgress}
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateAccuracy(false)}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors text-sm"
                  disabled={updateInProgress}
                >
                  Mark Inaccurate
                </button>
                <button
                  onClick={() => updateAccuracy(true)}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors text-sm"
                  disabled={updateInProgress}
                >
                  Mark Accurate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderUpdate;
